import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Link2, Unlink, Search, CheckCircle2, AlertCircle, ChevronRight } from 'lucide-react';
import ohipHotelGroupsService from '../services/OhipHotelGroupsService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import './OhipHotelLinkWizard.css';

// Admin > Billing > OHIP Transactions > Link hotels.
//
// OHIP measures usage per PROPERTY CODE and bills per HOTEL GROUP; nothing said which hub hotel a code is,
// so no group had anyone to bill. Two steps put that right:
//   1. Hotels  — link each OHIP code to a hub hotel. A hub hotel with the same code is offered as a
//                suggestion, but it is a suggestion only: codes are unique within OHIP, not across the hub's
//                chains (MIR is Malolo in one chain and Meeru in another), so a person confirms each link.
//   2. Billing — each group's bill-to is proposed from its linked hotels: one hotel → that hotel; several in
//                one chain → that chain; several chains → nothing is guessed, choose one.
// A full page, not a modal (STYLE.md), with the step in the URL so a reload or a shared link lands on it.

const STEPS = [
    { key: 'hotels', label: 'Link hotels' },
    { key: 'billing', label: 'Set billing' },
    { key: 'done', label: 'Done' },
];
const STEP_KEYS = STEPS.map(s => s.key);
const PICKER_LIMIT = 15;

const ownerKey = (type, id) => (type && id ? `${type}:${id}` : '');
const currentOwner = (g) => (g.hotelId ? ownerKey('hotel', g.hotelId)
    : g.accountId ? ownerKey('account', g.accountId)
    : g.partnerId ? ownerKey('partner', g.partnerId) : '');

function OhipHotelLinkWizard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const stepParam = searchParams.get('step');
    const step = STEP_KEYS.includes(stepParam) ? stepParam : 'hotels';
    const goStep = useCallback((key) => {
        const next = new URLSearchParams(searchParams);
        next.set('step', key);
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    // Step 1 state: unsaved link changes (code -> hotelId | null), the filter, and the row being picked for.
    const [pending, setPending] = useState(() => new Map());
    const [show, setShow] = useState('unlinked');
    const [search, setSearch] = useState('');
    const [picking, setPicking] = useState(null);
    const [pickerSearch, setPickerSearch] = useState('');

    // Step 2 state: groupId -> chosen owner key ('' = leave as it is).
    const [choices, setChoices] = useState(() => new Map());
    const [groupShow, setGroupShow] = useState('changes');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await ohipHotelGroupsService.hotelLinks();
            setData(res.data);
            setPending(new Map());
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    // ── Lookups ─────────────────────────────────────────────────────────────────────────────────────────
    const hubById = useMemo(() => new Map((data?.hubHotels || []).map(h => [h.hotelId, h])), [data]);
    const hubByCode = useMemo(() => {
        const m = new Map();
        for (const h of data?.hubHotels || []) {
            const c = String(h.hotelCode || '').trim().toUpperCase();
            if (!c) continue;
            if (!m.has(c)) m.set(c, []);
            m.get(c).push(h);
        }
        return m;
    }, [data]);
    const accountById = useMemo(() => new Map((data?.accounts || []).map(a => [a.accountId, a])), [data]);
    const partnerById = useMemo(() => new Map((data?.partners || []).map(p => [p.partnerId, p])), [data]);

    const linkOf = useCallback((row) => (pending.has(row.hotelCode) ? pending.get(row.hotelCode) : row.hotelId), [pending]);
    const suggestionsOf = useCallback((row) => hubByCode.get(row.hotelCode.toUpperCase()) || [], [hubByCode]);

    const hotelLabel = (h) => (h ? `${h.hotelName}${h.accountName ? ` — ${h.accountName}` : ''}` : '');
    const ownerLabel = (key) => {
        if (!key) return 'Not set';
        const [type, raw] = key.split(':');
        const id = parseInt(raw, 10);
        if (type === 'hotel') return `Hotel: ${hotelLabel(hubById.get(id)) || id}`;
        if (type === 'account') return `Chain: ${accountById.get(id)?.accountName || id}`;
        if (type === 'partner') return `Partner: ${partnerById.get(id)?.partnerName || id}`;
        return key;
    };

    // ── Step 1: hotels ──────────────────────────────────────────────────────────────────────────────────
    const hotelRows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return (data?.hotels || []).filter(r => {
            const linked = !!linkOf(r);
            if (show === 'unlinked' && linked) return false;
            if (show === 'suggested' && (linked || !suggestionsOf(r).length)) return false;
            if (!needle) return true;
            const h = hubById.get(linkOf(r));
            return [r.hotelCode, r.environmentName, r.tenancyId, ...r.groups.map(g => g.name), h?.hotelName, h?.accountName]
                .some(v => String(v || '').toLowerCase().includes(needle));
        });
    }, [data, show, search, linkOf, suggestionsOf, hubById]);

    const counts = useMemo(() => {
        const all = data?.hotels || [];
        const linked = all.filter(r => linkOf(r)).length;
        const oneSuggestion = all.filter(r => !linkOf(r) && suggestionsOf(r).length === 1).length;
        return { all: all.length, linked, unlinked: all.length - linked, oneSuggestion };
    }, [data, linkOf, suggestionsOf]);

    const setLink = (code, hotelId) => {
        setPending(prev => {
            const next = new Map(prev);
            const saved = (data.hotels.find(r => r.hotelCode === code) || {}).hotelId || null;
            if ((hotelId || null) === saved) next.delete(code); else next.set(code, hotelId || null);
            return next;
        });
        setPicking(null);
        setPickerSearch('');
    };

    // Only where there is exactly ONE hub hotel with the code: two candidates is a decision, not a default.
    const acceptSingleSuggestions = () => {
        setPending(prev => {
            const next = new Map(prev);
            for (const r of data.hotels) {
                const s = suggestionsOf(r);
                if (!linkOf(r) && s.length === 1) next.set(r.hotelCode, s[0].hotelId);
            }
            return next;
        });
    };

    const pickerResults = useMemo(() => {
        const needle = pickerSearch.trim().toLowerCase();
        if (!needle) return [];
        return (data?.hubHotels || []).filter(h =>
            [h.hotelName, h.hotelCode, h.accountName].some(v => String(v || '').toLowerCase().includes(needle))
        ).slice(0, PICKER_LIMIT);
    }, [data, pickerSearch]);

    const saveLinks = async () => {
        setBusy(true);
        setError(null);
        try {
            if (pending.size) {
                const links = [...pending.entries()].map(([hotelCode, hotelId]) => ({ hotelCode, hotelId }));
                await ohipHotelGroupsService.saveHotelLinks(links);
                await load();
            }
            setChoices(new Map());
            goStep('billing');
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setBusy(false);
        }
    };

    // ── Step 2: billing ─────────────────────────────────────────────────────────────────────────────────
    // Proposals are worked out from SAVED links only — step 1 saves before moving here.
    const groupRows = useMemo(() => {
        if (!data) return [];
        const linkByCode = new Map(data.hotels.map(r => [r.hotelCode, r.hotelId]));
        return data.groups.map(g => {
            const codes = String(g.codes || '').split(',').map(c => c.trim()).filter(Boolean);
            const hotels = [...new Set(codes.map(c => linkByCode.get(c)).filter(Boolean))].map(id => hubById.get(id)).filter(Boolean);
            const accountIds = [...new Set(hotels.map(h => h.accountId).filter(Boolean))];
            const partnerIds = [...new Set(accountIds.map(a => accountById.get(a)?.partnerId).filter(Boolean))];
            const linkedCount = codes.filter(c => linkByCode.get(c)).length;
            let proposal = '';
            let why = '';
            if (!hotels.length) why = 'No linked hotels';
            else if (codes.length === 1 && hotels.length === 1) { proposal = ownerKey('hotel', hotels[0].hotelId); why = 'One hotel'; }
            else if (accountIds.length === 1) { proposal = ownerKey('account', accountIds[0]); why = 'All linked hotels in one chain'; }
            else why = 'Hotels in several chains — choose';
            const options = [
                ...hotels.map(h => ownerKey('hotel', h.hotelId)),
                ...accountIds.map(a => ownerKey('account', a)),
                ...partnerIds.map(p => ownerKey('partner', p)),
            ];
            const current = currentOwner(g);
            if (current && !options.includes(current)) options.unshift(current);
            return { group: g, codes, linkedCount, proposal, why, options, current };
        });
    }, [data, hubById, accountById]);

    // Default each group to its proposal where that would change something; otherwise leave it alone.
    useEffect(() => {
        if (step !== 'billing' || !groupRows.length) return;
        setChoices(prev => {
            if (prev.size) return prev;
            const next = new Map();
            for (const r of groupRows) if (r.proposal && r.proposal !== r.current) next.set(r.group.id, r.proposal);
            return next;
        });
    }, [step, groupRows]);

    const choiceOf = (r) => (choices.has(r.group.id) ? choices.get(r.group.id) : '');
    const changes = groupRows.filter(r => choiceOf(r) && choiceOf(r) !== r.current);
    const visibleGroups = groupShow === 'changes'
        ? groupRows.filter(r => (r.proposal && r.proposal !== r.current) || choices.has(r.group.id) || (!r.current && r.options.length))
        : groupRows;

    const applyBilling = async () => {
        setBusy(true);
        setError(null);
        try {
            const groups = changes.map(r => {
                const [entityType, entityId] = choiceOf(r).split(':');
                return { id: r.group.id, entityType, entityId: parseInt(entityId, 10) };
            });
            if (groups.length) await ohipHotelGroupsService.attributeGroups(groups);
            setResult({ groups: groups.length, linked: counts.linked, unlinked: counts.unlinked });
            await load();
            goStep('done');
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setBusy(false);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────────────────────────────────
    const back = () => navigate('/admin/billing/ohip-transactions');

    return (
        <div className="olw-page">
            <Button variant="secondary" onClick={back}><ArrowLeft className="icon" /> Back</Button>

            <div className="olw-header">
                <h1>Link OHIP hotels</h1>
                <p className="olw-subtitle">
                    Connect each OHIP property to a hotel in the hub, then set who each OHIP billing group is billed to.
                </p>
                <div className="olw-steps">
                    {STEPS.map((s, i) => (
                        <span key={s.key} className={`olw-step ${s.key === step ? 'olw-step-active' : ''}`}>
                            <span className="olw-step-num">{i + 1}</span> {s.label}
                            {i < STEPS.length - 1 && <ChevronRight size={14} className="olw-step-sep" />}
                        </span>
                    ))}
                </div>
            </div>

            {error && <div className="olw-error"><AlertCircle size={16} /> {error}</div>}
            {loading && <div className="olw-loading"><Spinner /></div>}

            {!loading && data && step === 'hotels' && (
                <div className="olw-card">
                    <div className="olw-summary">
                        <span><strong>{counts.all}</strong> OHIP hotels</span>
                        <span><strong>{counts.linked}</strong> linked</span>
                        <span><strong>{counts.unlinked}</strong> not linked</span>
                        {pending.size > 0 && <span className="olw-pending">{pending.size} unsaved change(s)</span>}
                    </div>
                    <div className="olw-controls">
                        <label className="olw-grow">
                            <Search size={14} />
                            <input placeholder="Search code, environment, group, hotel or chain" value={search} onChange={e => setSearch(e.target.value)} />
                        </label>
                        <select value={show} onChange={e => setShow(e.target.value)}>
                            <option value="unlinked">Not linked</option>
                            <option value="suggested">Not linked, with a suggestion</option>
                            <option value="all">All</option>
                        </select>
                        <Button variant="secondary" onClick={acceptSingleSuggestions} disabled={!counts.oneSuggestion}>
                            <Link2 className="icon" /> Accept {counts.oneSuggestion} single suggestion(s)
                        </Button>
                    </div>

                    <table className="olw-table">
                        <thead>
                            <tr><th>OHIP code</th><th>Environment</th><th>Billing group</th><th>Hub hotel</th></tr>
                        </thead>
                        <tbody>
                            {hotelRows.map(r => {
                                const linkedId = linkOf(r);
                                const linked = linkedId ? hubById.get(linkedId) : null;
                                const sugg = suggestionsOf(r).filter(h => h.hotelId !== linkedId);
                                const changed = pending.has(r.hotelCode);
                                return (
                                    <tr key={r.hotelCode} className={changed ? 'olw-row-changed' : ''}>
                                        <td><code>{r.hotelCode}</code></td>
                                        <td>
                                            {r.environmentName || <span className="olw-muted">not discovered</span>}
                                            {r.category && r.category !== 'Production' && <span className="olw-chip olw-chip-warn">{r.category}</span>}
                                        </td>
                                        <td>{r.groups.length ? r.groups.map(g => <span key={g.id} className="olw-chip">{g.name}</span>) : <span className="olw-muted">none</span>}</td>
                                        <td>
                                            {linked && (
                                                <div className="olw-linked">
                                                    <span>{hotelLabel(linked)}{linked.hotelCode && linked.hotelCode.toUpperCase() !== r.hotelCode.toUpperCase() && <span className="olw-chip">code {linked.hotelCode}</span>}</span>
                                                    <button type="button" className="olw-link-btn" onClick={() => setLink(r.hotelCode, null)} title="Remove link"><Unlink size={14} /></button>
                                                </div>
                                            )}
                                            {!linked && sugg.map(h => (
                                                <button key={h.hotelId} type="button" className="olw-suggest" onClick={() => setLink(r.hotelCode, h.hotelId)}>
                                                    <Link2 size={13} /> {hotelLabel(h)}
                                                </button>
                                            ))}
                                            {picking === r.hotelCode ? (
                                                <div className="olw-picker">
                                                    <input autoFocus placeholder="Search hub hotels by name, code or chain" value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} />
                                                    {pickerResults.map(h => (
                                                        <button key={h.hotelId} type="button" className="olw-pick" onClick={() => setLink(r.hotelCode, h.hotelId)}>
                                                            {hotelLabel(h)} {h.hotelCode && <code>{h.hotelCode}</code>}
                                                        </button>
                                                    ))}
                                                    {pickerSearch.trim() && !pickerResults.length && <div className="olw-muted">No hub hotel matches.</div>}
                                                    <button type="button" className="olw-link-btn" onClick={() => { setPicking(null); setPickerSearch(''); }}>Cancel</button>
                                                </div>
                                            ) : (
                                                <button type="button" className="olw-link-btn" onClick={() => { setPicking(r.hotelCode); setPickerSearch(''); }}>
                                                    {linked ? 'Change' : 'Choose…'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {!hotelRows.length && <tr><td colSpan={4} className="olw-empty">Nothing to show for this filter.</td></tr>}
                        </tbody>
                    </table>

                    <div className="olw-footer">
                        <Button variant="secondary" onClick={() => setPending(new Map())} disabled={!pending.size || busy}>Discard changes</Button>
                        <Button variant="success" onClick={saveLinks} disabled={busy}>
                            {pending.size ? `Save ${pending.size} link(s) and continue` : 'Continue to billing'}
                        </Button>
                    </div>
                </div>
            )}

            {!loading && data && step === 'billing' && (
                <div className="olw-card">
                    <div className="olw-summary">
                        <span><strong>{groupRows.length}</strong> billing groups</span>
                        <span><strong>{groupRows.filter(r => r.current).length}</strong> with a bill-to</span>
                        <span><strong>{changes.length}</strong> to change</span>
                    </div>
                    <div className="olw-controls">
                        <select value={groupShow} onChange={e => setGroupShow(e.target.value)}>
                            <option value="changes">Proposed changes and unset groups</option>
                            <option value="all">All groups</option>
                        </select>
                    </div>
                    <table className="olw-table">
                        <thead>
                            <tr><th>Billing group</th><th>Hotels linked</th><th>Billed to now</th><th>Bill to</th></tr>
                        </thead>
                        <tbody>
                            {visibleGroups.map(r => (
                                <tr key={r.group.id} className={choiceOf(r) && choiceOf(r) !== r.current ? 'olw-row-changed' : ''}>
                                    <td>{r.group.name}{r.group.environmentNames && <div className="olw-muted">{r.group.environmentNames}</div>}</td>
                                    <td>{r.linkedCount} of {r.codes.length}<div className="olw-muted">{r.why}</div></td>
                                    <td>{ownerLabel(r.current)}</td>
                                    <td>
                                        <select value={choiceOf(r)} disabled={!r.options.length}
                                            onChange={e => setChoices(prev => { const n = new Map(prev); n.set(r.group.id, e.target.value); return n; })}>
                                            <option value="">{r.current ? 'Leave as it is' : '— not set —'}</option>
                                            {r.options.map(o => <option key={o} value={o}>{ownerLabel(o)}{o === r.proposal ? ' (proposed)' : ''}</option>)}
                                        </select>
                                    </td>
                                </tr>
                            ))}
                            {!visibleGroups.length && <tr><td colSpan={4} className="olw-empty">No groups need a bill-to.</td></tr>}
                        </tbody>
                    </table>
                    <div className="olw-footer">
                        <Button variant="secondary" onClick={() => goStep('hotels')} disabled={busy}><ArrowLeft className="icon" /> Link hotels</Button>
                        <Button variant="success" onClick={applyBilling} disabled={busy || !changes.length}>
                            Set bill-to on {changes.length} group(s)
                        </Button>
                    </div>
                </div>
            )}

            {!loading && data && step === 'done' && (
                <div className="olw-card olw-done">
                    <CheckCircle2 size={28} className="olw-done-icon" />
                    <h2>Billing links saved</h2>
                    {result ? (
                        <ul>
                            <li>{result.groups} billing group(s) given a bill-to</li>
                            <li>{result.linked} OHIP hotel(s) linked, {result.unlinked} not linked</li>
                        </ul>
                    ) : (
                        <p className="olw-muted">{counts.linked} of {counts.all} OHIP hotels are linked.</p>
                    )}
                    <div className="olw-footer">
                        <Button variant="secondary" onClick={() => goStep('hotels')}>Link more hotels</Button>
                        <Button variant="success" onClick={back}>Back to OHIP Transactions</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OhipHotelLinkWizard;
